'use client';

import { Button, Heading } from '@petsmart-ui/sparky';
import { type ReactElement, useState } from 'react';

import { RemoveCardModal } from '../RemoveCardModal';
import { AddPaymentForm } from './AddPaymentForm';
import { EditPaymentForm } from './EditPaymentForm';
import type { PaymentFormProps } from './types';

import './PaymentForm.scss';

export function PaymentForm({
    title = 'Payments',
    subtitle = 'Add credit or debit card',
    isDefaultCheckDisabled,
    isDefaultCheckChecked,
    setBillingAddressAsDefaultShippingInMarketOnly,
    editPaymentData,
    onCloseForm,
    onSuccess,
    onError,
    defaultAddressId,
    onEditSubmit,
    onAddressValidationApiError,
    disableUntilDirty,
    addAddressIconSize,
    onRemoveCardClick,
    onExperianModalVisibilityChange = () => {},
    onClearError,
    hideCancelCta,
    submitCtaText,
}: Readonly<PaymentFormProps>): ReactElement {
    const [openModal, setOpenModal] = useState<boolean>(false);

    return (
        <div className="payment-form" data-testid="payment-form">
            {title && (
                <Heading tagName="h5" data-testid="payment-form-title">
                    {title}
                </Heading>
            )}

            {subtitle && (
                <Heading
                    tagName="h6"
                    size="body-lg-bold"
                    data-testid="payment-form-subtitle"
                    className={editPaymentData ? 'payment-form__subtitle' : ''}
                >
                    {editPaymentData ? 'Edit card' : subtitle}
                </Heading>
            )}

            {editPaymentData ? (
                <EditPaymentForm
                    isDefaultCheckDisabled={isDefaultCheckDisabled || false}
                    editPaymentData={editPaymentData}
                    onCloseForm={onCloseForm}
                    onSuccess={isDefault => onSuccess({ isEdit: true, isDefault })}
                    onError={onError}
                    onClearError={onClearError}
                    onSubmit={onEditSubmit}
                    onAddressValidationApiError={onAddressValidationApiError}
                    disableUntilDirty={disableUntilDirty}
                    addAddressIconSize={addAddressIconSize}
                    defaultAddressId={defaultAddressId}
                    onExperianModalVisibilityChange={onExperianModalVisibilityChange}
                />
            ) : (
                <AddPaymentForm
                    isDefaultCheckDisabled={isDefaultCheckDisabled || false}
                    isDefaultCheckChecked={isDefaultCheckChecked || false}
                    setBillingAddressAsDefaultShippingInMarketOnly={
                        setBillingAddressAsDefaultShippingInMarketOnly || false
                    }
                    onCloseForm={onCloseForm}
                    onSuccess={args =>
                        onSuccess({
                            isEdit: false,
                            isDefault: args?.isDefault,
                            addressToLink: args?.addressToLink,
                        })
                    }
                    onError={onError}
                    onClearError={onClearError}
                    defaultAddressId={defaultAddressId}
                    addAddressIconSize={addAddressIconSize}
                    onExperianModalVisibilityChange={onExperianModalVisibilityChange}
                    onAddressValidationApiError={onAddressValidationApiError}
                    hideCancelCta={hideCancelCta}
                    submitCtaText={submitCtaText}
                />
            )}

            {editPaymentData && (
                <>
                    <div className="payment-form__remove-button" data-testid="payment-form-remove-button">
                        <Button
                            text="Remove card"
                            variant="link"
                            onClick={() => (onRemoveCardClick ? onRemoveCardClick() : setOpenModal(true))}
                        />
                    </div>
                    <RemoveCardModal
                        currentPayment={editPaymentData}
                        modalOpen={openModal}
                        modalClose={(hideForm?: boolean) => {
                            setOpenModal(false);
                            if (hideForm) {
                                onCloseForm();
                            }
                        }}
                    />
                </>
            )}
        </div>
    );
}
